/**
 * Vendure refuses any list query asking for more than this in one `take`,
 * answering `Cannot take more than 500 results from a list query` with a null
 * `data` — which `makeGraphqlRequest` turns into a thrown 500.
 */
export const LIST_QUERY_MAX_TAKE = 500

/**
 * The tighter of the two ceilings an `id.in` request must fit under. Vendure's
 * `take` cap above is one; the other is CloudFront's WAF (AWS managed Core
 * Rule Set, SizeRestrictions_QUERYSTRING), which 403s any request whose query
 * string exceeds 2048 bytes — and the GraphQL middleware sends query variables
 * URL-encoded in a GET query string, where every id costs ~12-14 bytes. At 500
 * ids per batch the enrichment queries crossed that at `?page=7` (168 ids ≈
 * 2.3KB) and every sale badge and hover image silently vanished. 120 ids
 * encode to ~1.7KB with ids up to 7 digits, leaving headroom for the
 * middleware's own hash/context params.
 */
export const ID_FILTER_CHUNK_SIZE = 120

/**
 * Split an id list into batches a list query will actually accept.
 *
 * The listings enrich their grid by re-querying every product on screen by id
 * (sale pricing, hover-state alt images). While the grid was one page of 24
 * that was always a single small request; under "Load more" the grid grows
 * without bound, and at 504 products the enrichment query crossed Vendure's
 * `take` ceiling and failed *wholesale* — every card lost its sale badge and
 * old price, including the ones that had been showing them a moment earlier.
 * The WAF's query-string limit (see `ID_FILTER_CHUNK_SIZE`) is the same
 * failure mode with a lower threshold.
 *
 * Batching keeps each request legal, so enrichment degrades product-by-product
 * (at worst) instead of vanishing for the whole listing at a threshold.
 */
export function chunkIds<T>(ids: T[], size: number = ID_FILTER_CHUNK_SIZE): T[][] {
  if (ids.length <= size)
    return ids.length ? [ids] : []

  const batches: T[][] = []
  for (let i = 0; i < ids.length; i += size)
    batches.push(ids.slice(i, i + size))
  return batches
}
